/**
 * Resolves player public config into a normalized internal shape.
 * Application state must not leak into the engine — pass structured config only.
 */

import { resolveFeatureFlags } from "../Utilities/featureFlags";
import { detectMediaType } from "../Core/MediaTypes";

/**
 * @param {object} config
 * @returns {object}
 */
export function resolvePlayerConfig(config) {
  config = config || {};
  var source = config.source;
  if (typeof source === "string") {
    source = { src: source, type: detectMediaType(source) };
  } else if (source && source.src) {
    var explicitType = source.type;
    var normalizedType = explicitType;
    if (typeof explicitType === "string") {
      var t = explicitType.toLowerCase();
      if (
        t === "hls" ||
        t.indexOf("mpegurl") !== -1 ||
        t === "application/x-mpegurl"
      ) {
        normalizedType = "hls";
      } else if (t === "mp4" || t === "video/mp4" || t.indexOf("mp4") !== -1) {
        normalizedType = "mp4";
      } else if (t === "youtube" || t.indexOf("youtube") !== -1) {
        normalizedType = "youtube";
      } else if (t === "vimeo" || t.indexOf("vimeo") !== -1) {
        normalizedType = "vimeo";
      }
    }
    source = Object.assign({}, source, {
      type: normalizedType || detectMediaType(source.src),
    });
    if (source.type === "unknown") {
      source.type = detectMediaType(source.src);
    }
  } else {
    source = null;
  }

  return {
    source: source,
    poster: config.poster || (source && source.poster) || "",
    title: config.title || "",
    subtitles: Array.isArray(config.subtitles) ? config.subtitles : [],
    audioTracks: Array.isArray(config.audioTracks) ? config.audioTracks : [],
    chapters: Array.isArray(config.chapters) ? config.chapters : [],
    contentId: config.contentId || null,
    plugins: Array.isArray(config.plugins) ? config.plugins : [],
    branding: Object.assign(
      {
        label: "",
        accent: "",
        logo: "",
        watermark: "",
        watermarkPosition: "bottom-right",
        watermarkOpacity: 0.85,
        watermarkSize: 12,
        watermarkFadeIn: true,
      },
      config.branding || {}
    ),
    theme: config.theme || {},
    features: resolveFeatureFlags(config.features || config.featureFlags),
    /**
     * White-label casting options (receiver id, provider branding, simulate).
     * @example { receiverApplicationId, chromecast, airplay, simulate, brand }
     */
    casting: Object.assign(
      {
        receiverApplicationId: "",
        chromecast: true,
        airplay: true,
        simulate: false,
        brand: {},
        chromecastOptions: {},
        airplayOptions: {},
        simulateOptions: {},
        remoteKeyMap: null,
      },
      config.casting || {}
    ),
    autoplay: !!config.autoplay,
    muted: config.muted != null ? !!config.muted : !!config.autoplay,
    startTime: config.startTime || 0,
    playbackRate: config.playbackRate || 1,
    volume: config.volume != null ? config.volume : 1,
    debug: !!config.debug,
    controls: config.controls || {},
    sessionId: config.sessionId || null,
    sessionStartedAt: config.sessionStartedAt || null,
    mediaAsset: config.mediaAsset || null,
    presentationStrategy: config.presentationStrategy || null,
    enabledCapabilities: Array.isArray(config.enabledCapabilities)
      ? config.enabledCapabilities
      : [],
  };
}

export default resolvePlayerConfig;
